import React, { useState, useEffect } from 'react';
import './ContactForm.scss';
import icon from '../../assets/banner/icons/Calling.webp';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SERVICES = ['Dentures', 'Implants', 'Whitening', 'Root Canal'];
const DEPARTMENTS = ['General', 'Cosmetic', 'Orthodontics', 'Surgery'];

const ContactForm = () => {
    const [form, setForm] = useState({
        patient_name: '',
        patient_email: '',
        service: '',
        department: '',
        date: '',
        slot_id: '',
        message: '',
    });
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null); // { success, message }

    // Fetch available slots when date or service changes
    useEffect(() => {
        if (!form.date) {
            setSlots([]);
            return;
        }
        const fetchSlots = async () => {
            setLoadingSlots(true);
            setSlots([]);
            setForm(prev => ({ ...prev, slot_id: '' }));
            try {
                const params = new URLSearchParams({ date: form.date });
                if (form.service) params.append('service', form.service);
                const res = await fetch(`${API_URL}/api/slots?${params}`);
                if (res.ok) {
                    const data = await res.json();
                    setSlots(data.filter(s => s.is_available));
                }
            } catch {
                // silently fail — slots stay empty
            } finally {
                setLoadingSlots(false);
            }
        };
        fetchSlots();
    }, [form.date, form.service]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setSubmitResult(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.slot_id) {
            setSubmitResult({ success: false, message: 'Please select an available time slot.' });
            return;
        }
        setSubmitting(true);
        setSubmitResult(null);
        try {
            const res = await fetch(`${API_URL}/api/appointments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    slot_id: form.slot_id,
                    patient_name: form.patient_name,
                    patient_email: form.patient_email,
                    service: form.service,
                    department: form.department,
                    message: form.message,
                }),
            });
            if (res.ok) {
                setSubmitResult({
                    success: true,
                    message: 'Appointment booked! Check your email for confirmation details.',
                });
                setForm({
                    patient_name: '', patient_email: '', service: '',
                    department: '', date: '', slot_id: '', message: '',
                });
                setSlots([]);
            } else {
                const err = await res.json();
                setSubmitResult({ success: false, message: err.detail || 'Booking failed. Please try again.' });
            }
        } catch {
            setSubmitResult({ success: false, message: 'Network error. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];

    return (
        <form onSubmit={handleSubmit}>
            <div className="row">
                {/* Name */}
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            name="patient_name"
                            className="form-control"
                            placeholder="Enter your name..."
                            value={form.patient_name}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                {/* Email */}
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>E-mail Address</label>
                        <input
                            type="email"
                            name="patient_email"
                            className="form-control"
                            placeholder="Enter email address..."
                            value={form.patient_email}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                {/* Service */}
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>Service</label>
                        <select
                            name="service"
                            className="form-control"
                            value={form.service}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Service</option>
                            {SERVICES.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Department */}
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>Department</label>
                        <select
                            name="department"
                            className="form-control"
                            value={form.department}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Department</option>
                            {DEPARTMENTS.map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Date */}
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>Preferred Date</label>
                        <input
                            type="date"
                            name="date"
                            className="form-control"
                            min={today}
                            value={form.date}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                {/* Time Slot */}
                <div className="col-lg-6">
                    <div className="form-group">
                        <label>Available Time Slot</label>
                        <select
                            name="slot_id"
                            className="form-control"
                            value={form.slot_id}
                            onChange={handleChange}
                            required
                            disabled={!form.date || loadingSlots}
                        >
                            {loadingSlots ? (
                                <option value="">Loading slots...</option>
                            ) : !form.date ? (
                                <option value="">Select a date first</option>
                            ) : slots.length === 0 ? (
                                <option value="">No slots available</option>
                            ) : (
                                <>
                                    <option value="">Choose a time slot</option>
                                    {slots.map(slot => (
                                        <option key={slot._id} value={slot._id}>
                                            {slot.start_time} – {slot.end_time} | {slot.doctor_name}
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>
                </div>

                {/* Message */}
                <div className="col-lg-12">
                    <div className="form-group">
                        <label>Messages</label>
                        <textarea
                            name="message"
                            className="form-control"
                            placeholder="Enter your messages..."
                            rows="3"
                            value={form.message}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Feedback */}
                {submitResult && (
                    <div className="col-lg-12">
                        <div
                            className={`alert ${submitResult.success ? 'alert-success' : 'alert-danger'}`}
                            role="alert"
                        >
                            {submitResult.message}
                        </div>
                    </div>
                )}

                {/* Submit */}
                <div className="col-lg-6">
                    <button
                        type="submit"
                        className="btn appointment-btn"
                        disabled={submitting}
                    >
                        {submitting ? 'Booking...' : 'Book an appointment'}
                    </button>
                </div>

                {/* Emergency call */}
                <div className="col-lg-6">
                    <div className="appointment-call">
                        <div className="icon">
                            <img src={icon} alt="icon"  loading="lazy" />
                        </div>
                        <div className="call-text">
                            <p>Dental 24H Emergency</p>
                            <h6>03 482 394 123</h6>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default ContactForm;
